import React from 'react';
import { ExternalSystemId, ModelDTO } from '../../api/useModelGroupsQuery';
import { ModalHeader } from '../../../../../CommonComponents/OPL/Modal/ModalHeader';
import { ModalCloseButton } from '../../../../../CommonComponents/OPL/Modal/ModalCloseButton';
import { ModalFooter } from '../../../../../CommonComponents/OPL/Modal/ModalFooter';
import { Button } from '../../../../../CommonComponents/OPL/Button/Button';
import { Input } from '../../../../../CommonComponents/OPL/Input/Input';
import { Modal } from '../../../../../CommonComponents/OPL/Modal/Modal';
import { Textarea } from '../../../../../CommonComponents/OPL/Textarea/Textarea';
import {
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '../../../../../CommonComponents/OPL/Table/Table';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { Dot } from '../../../../../CommonComponents/OPL/Dot/Dot';
import { Label } from '../../../../../CommonComponents/OPL/Label/Label';
import { HelperText } from '../../../../../CommonComponents/OPL/HelperText/HelperText';
import { useUpsertModel } from './useUpsertModel';
import { formatNumber } from '../../../../../CommonUtils/formatNumber';
import { columns } from './UpsertModel.constants';
import { UpsertModelGroupActions } from '../UpsertModelGroup/useUpsertModelGroup';

interface Props {
  model: Partial<ModelDTO>;
  isReadOnly?: boolean;
  models: ModelDTO[];
  modelsActions?: UpsertModelGroupActions;
  externalSystem?: ExternalSystemId | null;
  onClose(): void;
}

export const UpsertModel: React.FC<Props> = ({
  model,
  isReadOnly,
  models,
  modelsActions,
  externalSystem,
  onClose,
}) => {
  const {
    formBag: {
      handleSubmit,
      register,
      formState: { errors },
      getValues,
      setValue,
    },
    allocationFields,
    assetColors,
    isAllocationInvalid,
    allocationTotal,
    onSubmit,
  } = useUpsertModel({ model, isReadOnly, models, modelsActions, onClose });

  return (
    <Modal
      isOpen
      size="large"
      onRequestClose={onClose}
      centered={false}
      contentLabel={`${model.id ? 'Edit' : 'Create'} Model`}
    >
      <ModalHeader>{model.id ? 'Edit' : 'Create'} Model</ModalHeader>
      <ModalCloseButton onClick={onClose} />
      <Box as="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <input {...register('orionModelId')} type="hidden" />

        <Box px="24" pb="24">
          <Input
            {...register('modelName')}
            name="modelName"
            label="Model Name"
            type="text"
            formVariant={errors.modelName ? 'error' : undefined}
            helperText={errors.modelName?.message}
            autoFocus
            readOnly={externalSystem === ExternalSystemId.Orion}
            disabled={isReadOnly}
            maxLength={256}
            mb="24"
          />

          <Textarea
            {...register('modelDescription')}
            name="modelDescription"
            label="Description"
            rows={4}
            disabled={isReadOnly}
            resize={isReadOnly ? 'none' : 'vertical'}
            maxLength={5000}
            mb="24"
          />

          <Input
            {...register('hypotheticalReturn')}
            name="hypotheticalReturn"
            label="Straight-line Projected Return"
            type="number"
            rightIcon="far fa-percent"
            formVariant={errors.hypotheticalReturn ? 'error' : undefined}
            helperText={errors.hypotheticalReturn?.message}
            disabled={isReadOnly}
            onBlur={() => {
              const value = getValues('hypotheticalReturn');

              const formattedValue =
                value !== ''
                  ? formatNumber(Number(value), {
                      decimalPlaces: 2,
                      thousandSeparated: false,
                    })
                  : '';

              setValue('hypotheticalReturn', formattedValue);
            }}
          />
        </Box>

        <Label ml="24" mb="12" mt="16">
          Monte Carlo Simulation Asset Allocation
        </Label>

        <TableHeader>
          <TableRow>
            <TableCell {...columns.name}>
              <Text>Name</Text>
            </TableCell>

            <TableCell {...columns.value}>
              <Text>Value</Text>
            </TableCell>
          </TableRow>
        </TableHeader>

        <TableBody overflowY="auto" maxHeight={265}>
          {allocationFields.map((field, index) => {
            return (
              <TableRow key={field.id}>
                <input
                  {...register(`assetAllocation.${index}.id` as const)}
                  type="hidden"
                  defaultValue={field.id}
                />

                <TableCell {...columns.name}>
                  {assetColors && <Dot bg={assetColors[index]} mr="4" />}

                  <Text fontWeight={500} color="neutral.600">
                    {field.name}
                  </Text>
                </TableCell>

                <TableCell {...columns.value}>
                  <Input
                    key={field.key}
                    {...register(`assetAllocation.${index}.y` as const)}
                    defaultValue={field.y}
                    type="number"
                    placeholder="0"
                    sizeVariant="small"
                    disabled={isReadOnly}
                    readOnly={externalSystem === ExternalSystemId.Orion}
                    rightIcon="far fa-percent"
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>

        <Box
          display="flex"
          flexDirection="column"
          borderColor="neutral.200"
          borderStyle="solid"
          borderWidth="1px 0"
          p="16"
        >
          <Box display="flex" justifyContent="space-between">
            <Text fontWeight={600} color="neutral.600">
              Total
            </Text>

            <Text fontWeight={600}>
              {formatNumber(allocationTotal, {
                decimalPlaces: 2,
                thousandSeparated: false,
              })}
              %
            </Text>
          </Box>

          {isAllocationInvalid && (
            <Box alignSelf="flex-end">
              <HelperText variant="error" mt="4">
                The total must add up to 100%
              </HelperText>
            </Box>
          )}
        </Box>

        <ModalFooter>
          <Button variant="subtleInactive" onClick={onClose}>
            {isReadOnly ? 'Close' : 'Cancel'}
          </Button>

          {!isReadOnly && (
            <Button variant="primary" ml="12" type="submit">
              {model.id ? 'Ok' : 'Add Model'}
            </Button>
          )}
        </ModalFooter>
      </Box>
    </Modal>
  );
};
